import { FC, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CalendarIcon,
  CalendarDotsIcon,
  PlusIcon,
  CaretLeft,
  CaretRight,
} from '@phosphor-icons/react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import LeopartLoader from '../../shared/LeopartLoader/LeopartLoader';
import CustomButton from '../../shared/CustomButton/CustomButton';
import Typhography from '../../shared/Typhography/Typhography';
import { WeekStrip } from './WeekStrip';
import { MonthCalendar } from './MonthCalendar';
import { CreateSessionModal } from './CreateSessionModal';
import {
  useCoachSessionWeekView,
  ViewMode,
} from './useCoachSessionWeekView';
import { useWeekDnd } from './useWeekDnd';
import {
  coachSessionsEndpoints,
  ICoachSessionListItem,
} from '../../endpoints/coachSessions';
import styles from './CoachSessionWeekView.module.scss';

interface CoachSessionWeekViewProps {
  /** Se passato, sovrascrive il teamId letto da useParams (per uso in embed). */
  teamId?: string;
}

/**
 * Vista settimanale/mensile delle sessioni di allenamento campo.
 * Spec: LEOPART_ProgrammazioneCampo_Spec_v1.1.1.md §2.
 * Route: /dashboard/teams/:teamId/sessions (standalone) o embed dentro Team Detail.
 */
const CoachSessionWeekView: FC<CoachSessionWeekViewProps> = ({
  teamId: propTeamId,
}) => {
  const params = useParams<{ teamId: string }>();
  const teamId = propTeamId ?? params.teamId;
  const navigate = useNavigate();
  const [modalDate, setModalDate] = useState<string | null>(null);

  const hook = useCoachSessionWeekView(teamId ?? '');
  const {
    viewMode,
    setViewMode,
    weekDays,
    weekLabel,
    monthLabel,
    sessions,
    sessionsByDate,
    monthCalendarDays,
    monthStart,
    isLoading,
    prevPeriod,
    nextPeriod,
    goToToday,
    goToDate,
    createMut,
    deleteMut,
    weeklyTotals,
    today,
  } = hook;

  const dnd = useWeekDnd(teamId ?? '', sessions);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDuplicate = (session: ICoachSessionListItem) => {
    const input = window.prompt(
      'Nuova data (YYYY-MM-DD):',
      session.scheduledDate.slice(0, 10),
    );
    if (!input) return;
    dnd.duplicateSession(session, input, session.startTime);
  };

  const handleMove = (session: ICoachSessionListItem) => {
    if (session.status === 'executed') {
      window.alert('Non è possibile spostare una sessione eseguita.');
      return;
    }
    const input = window.prompt(
      'Nuova data (YYYY-MM-DD):',
      session.scheduledDate.slice(0, 10),
    );
    if (!input) return;
    dnd.moveSession(session, input, session.startTime);
  };

  const handleDelete = (session: ICoachSessionListItem) => {
    const ok = window.confirm(
      `Eliminare la sessione "${session.title}"? L'operazione è reversibile (soft-delete).`,
    );
    if (!ok) return;
    deleteMut.mutate(session._id);
  };

  const handleExportPdf = async (session: ICoachSessionListItem) => {
    try {
      const blob = await coachSessionsEndpoints
        .get(session._id)
        .then(() => null);
      // Export PDF non ancora implementato lato backend per coach-session singola:
      // per ora apriamo dettaglio e invitiamo a usare "Esporta PDF" nell'header
      void blob;
      navigate(`/dashboard/teams/${teamId}/sessions/${session._id}/edit`);
    } catch {
      // noop
    }
  };

  const handleOpenCreateModal = (date: string) => setModalDate(date);

  const handleCreateSession = async (body: Parameters<typeof createMut.mutateAsync>[0]) => {
    const session = await createMut.mutateAsync(body);
    setModalDate(null);
    navigate(`/dashboard/teams/${teamId}/sessions/${session._id}/edit`);
  };

  const handleMonthDayClick = (date: string) => {
    // Centra vista settimanale sul giorno cliccato (spec sez 2 riga 146)
    goToDate(date);
    setViewMode('week' as ViewMode);
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.scrollArea}>
        <div className={styles.inner}>
          {/* ─── Toolbar ─────────────────────────────────────────── */}
          <div className={styles.toolbar}>
            <div className={styles.toolbarLeft}>
              <button
                className={styles.navBtn}
                onClick={prevPeriod}
                type="button"
                title="Periodo precedente"
              >
                <CaretLeft size={16} weight="bold" />
              </button>
              <button
                className={styles.navBtn}
                onClick={nextPeriod}
                type="button"
                title="Periodo successivo"
              >
                <CaretRight size={16} weight="bold" />
              </button>
              <Typhography type="h2" size="bold-16" className={styles.weekLabel}>
                {viewMode === 'week' ? weekLabel : monthLabel}
              </Typhography>
              <button
                className={styles.navBtn}
                onClick={goToToday}
                type="button"
                title="Oggi"
                style={{ width: 'auto', padding: '0 10px', fontFamily: 'Satoshi, sans-serif', fontSize: 12, fontWeight: 700 }}
              >
                Oggi
              </button>
            </div>

            <div className={styles.toolbarRight}>
              {/* ─── Toggle pill ───────────────────────────────── */}
              <div className={styles.togglePill}>
                <button
                  type="button"
                  className={`${styles.toggleBtn} ${
                    viewMode === 'week' ? styles.active : ''
                  }`}
                  onClick={() => setViewMode('week')}
                >
                  <CalendarIcon size={14} />
                  Settimana
                </button>
                <button
                  type="button"
                  className={`${styles.toggleBtn} ${
                    viewMode === 'month' ? styles.active : ''
                  }`}
                  onClick={() => setViewMode('month')}
                >
                  <CalendarDotsIcon size={14} />
                  Mese
                </button>
              </div>

              <CustomButton
                color="secondary"
                size="sm"
                iconPosition="left"
                icon={<PlusIcon size={14} weight="bold" />}
                onClick={() => handleOpenCreateModal(today.format('YYYY-MM-DD'))}
              >
                Nuova sessione
              </CustomButton>
            </div>
          </div>

          {/* ─── Loading ──────────────────────────────────────────── */}
          {isLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
              <LeopartLoader size="md" />
            </div>
          )}

          {/* ─── Week grid ────────────────────────────────────────── */}
          {!isLoading && viewMode === 'week' && (
            <DndContext
              sensors={sensors}
              onDragStart={dnd.handleDragStart}
              onDragEnd={dnd.handleDragEnd}
            >
              <WeekStrip
                weekDays={weekDays}
                sessionsByDate={sessionsByDate}
                teamId={teamId ?? ''}
                today={today}
                onCreateSession={handleOpenCreateModal}
                onDuplicate={handleDuplicate}
                onMove={handleMove}
                onDelete={handleDelete}
                onExportPdf={handleExportPdf}
              />
              {/* ─── Summary strip ─────────────────────────────── */}
              <div className={styles.summaryStrip}>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Durata totale</span>
                  <span className={styles.summaryValue}>
                    {weeklyTotals.duration}&#39;
                  </span>
                </div>
                <div className={styles.divider} />
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>EWT totale</span>
                  <span className={styles.summaryValue}>
                    {weeklyTotals.ewt}&#39;
                  </span>
                </div>
                <div className={styles.divider} />
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>CE totale</span>
                  <span className={styles.summaryValue}>
                    {weeklyTotals.ce.toFixed(1)}
                  </span>
                </div>
              </div>
              <div className={styles.dndHint}>
                Trascina una card per spostarla · Shift+trascina per duplicarla
                · Tasto destro per azioni rapide
              </div>
            </DndContext>
          )}

          {/* ─── Month calendar ───────────────────────────────────── */}
          {!isLoading && viewMode === 'month' && (
            <DndContext
              sensors={sensors}
              onDragStart={dnd.handleDragStart}
              onDragEnd={dnd.handleDragEnd}
            >
              <MonthCalendar
                days={monthCalendarDays}
                sessionsByDate={sessionsByDate}
                monthStart={monthStart}
                today={today}
                onDayClick={handleMonthDayClick}
                onDuplicate={handleDuplicate}
                onMove={handleMove}
                onDelete={handleDelete}
                onExportPdf={handleExportPdf}
              />
            </DndContext>
          )}
        </div>
      </div>

      {/* ─── Create session modal ─────────────────────────────────── */}
      {modalDate && (
        <CreateSessionModal
          teamId={teamId ?? ''}
          defaultDate={modalDate}
          isLoading={createMut.isPending}
          onConfirm={handleCreateSession}
          onClose={() => setModalDate(null)}
        />
      )}
    </div>
  );
};

export default CoachSessionWeekView;
